export const scrollObserver = (options = {}) => {
    // Ensure this function is not tree-shaken by webpack
    if (typeof window === 'undefined') return;
    
    // Configuration with defaults
    const config = {
        selector: '.inview',
        inoutSelector: '.inoutview',
        fullinoutSelector: '.fullinoutview',
        offset: 0.1, // 10% of viewport height
        throttleDelay: 16, // ~60fps
        ...options
    };

    // Cache elements and state
    let elements = [];
    let inoutElements = [];
    let fullinoutElements = [];
    let elementOptions = new Map(); // Store individual element options
    let inoutElementOptions = new Map(); // Store individual inout element options
    let fullinoutElementOptions = new Map(); // Store individual fullinout element options
    let isThrottled = false;
    let scrollHandler = null;

    // Parse data attributes from element
    const parseElementOptions = (element) => {
        const delay = parseInt(element.dataset.delay) || 0;
        const threshold = parseFloat(element.dataset.threshold) || config.offset;

        return {
            delay: Math.max(0, delay), // Ensure non-negative (milliseconds)
            threshold: Math.max(0, Math.min(1, threshold)) // Clamp between 0 and 1
        };
    };

    // Cache DOM elements and their options
    const cacheElements = () => {
        elements = Array.from(document.querySelectorAll(config.selector));
        inoutElements = Array.from(document.querySelectorAll(config.inoutSelector));
        fullinoutElements = Array.from(document.querySelectorAll(config.fullinoutSelector));

        elementOptions.clear();
        inoutElementOptions.clear();
        fullinoutElementOptions.clear();

        elements.forEach(element => {
            elementOptions.set(element, parseElementOptions(element));
        });

        inoutElements.forEach(element => {
            inoutElementOptions.set(element, parseElementOptions(element));
        });

        fullinoutElements.forEach(element => {
            fullinoutElementOptions.set(element, parseElementOptions(element));
        });
    };

    // Detect if element is in view (optimized)
    const checkInView = () => {
        const windowHeight = window.innerHeight;
        const scrollTop = window.scrollY;

        // Handle regular inview elements (one-time trigger)
        if (elements.length > 0) {
            elements.forEach(element => {
                if (!element.classList.contains('over')) {
                    const options = elementOptions.get(element);
                    const triggerPoint = scrollTop + windowHeight - (windowHeight * options.threshold);
                    const elemTop = element.getBoundingClientRect().top + scrollTop;

                    if (elemTop < triggerPoint) {
                        // Apply delay if specified
                        if (options.delay > 0) {
                            setTimeout(() => {
                                element.classList.add('over');
                            }, options.delay);
                        } else {
                            element.classList.add('over');
                        }
                    }
                }
            });
        }

        // Handle inoutview elements (toggle on scroll)
        if (inoutElements.length > 0) {
            inoutElements.forEach(element => {
                const options = inoutElementOptions.get(element);
                const triggerPoint = scrollTop + windowHeight - (windowHeight * options.threshold);
                const removePoint = scrollTop + windowHeight + (windowHeight * 0.1); // 30% past viewport
                const elemTop = element.getBoundingClientRect().top + scrollTop;
                const elemBottom = elemTop + element.getBoundingClientRect().height;

                const isInView = elemTop < triggerPoint && elemBottom > scrollTop;
                const shouldRemove = elemBottom < scrollTop || elemTop > removePoint;

                if (isInView && !element.classList.contains('over')) {
                    // Apply delay if specified
                    if (options.delay > 0) {
                        setTimeout(() => {
                            element.classList.add('over');
                        }, options.delay);
                    } else {
                        element.classList.add('over');
                    }
                } else if (shouldRemove && element.classList.contains('over')) {
                    element.classList.remove('over');
                }
            });
        }

        // Handle fullinoutview elements (fully contained in viewport)
        if (fullinoutElements.length > 0) {
            fullinoutElements.forEach(element => {
                const options = fullinoutElementOptions.get(element);
                const elemRect = element.getBoundingClientRect();
                const elemTop = elemRect.top + scrollTop;
                const elemBottom = elemTop + elemRect.height;
                const elemLeft = elemRect.left;
                const elemRight = elemLeft + elemRect.width;

                // Check if element is fully contained within viewport
                const isFullyInView = elemTop >= scrollTop &&
                    elemBottom <= scrollTop + windowHeight &&
                    elemLeft >= 0 &&
                    elemRight <= window.innerWidth;

                if (isFullyInView && !element.classList.contains('over')) {
                    // Apply delay if specified
                    if (options.delay > 0) {
                        setTimeout(() => {
                            element.classList.add('over');
                        }, options.delay);
                    } else {
                        element.classList.add('over');
                    }
                } else if (!isFullyInView && element.classList.contains('over')) {
                    element.classList.remove('over');
                }
            });
        }
    };

    // Throttled scroll handler
    const handleScroll = () => {
        if (isThrottled) return;

        isThrottled = true;
        requestAnimationFrame(() => {
            checkInView();
            isThrottled = false;
        });
    };

    // Initialize
    const init = () => {
        cacheElements();
        checkInView(); // Check initial state

        // Create bound handler for cleanup
        scrollHandler = handleScroll;
        window.addEventListener('scroll', scrollHandler, { passive: true });

        // Re-cache elements when DOM changes (optional optimization)
        if (window.MutationObserver) {
            const observer = new MutationObserver(() => {
                cacheElements();
            });
            observer.observe(document.body, { childList: true, subtree: true });
        }
    };

    // Public API
    const api = {
        // Refresh elements cache
        refresh: () => {
            cacheElements();
            checkInView();
        },

        // Add new element to tracking
        addElement: (element) => {
            if (element && !elements.includes(element)) {
                elements.push(element);
                elementOptions.set(element, parseElementOptions(element));
            }
        },

        // Add new inout element to tracking
        addInoutElement: (element) => {
            if (element && !inoutElements.includes(element)) {
                inoutElements.push(element);
                inoutElementOptions.set(element, parseElementOptions(element));
            }
        },

        // Add new fullinout element to tracking
        addFullinoutElement: (element) => {
            if (element && !fullinoutElements.includes(element)) {
                fullinoutElements.push(element);
                fullinoutElementOptions.set(element, parseElementOptions(element));
            }
        },

        // Remove element from tracking
        removeElement: (element) => {
            const index = elements.indexOf(element);
            if (index > -1) {
                elements.splice(index, 1);
                elementOptions.delete(element);
            }
        },

        // Remove inout element from tracking
        removeInoutElement: (element) => {
            const index = inoutElements.indexOf(element);
            if (index > -1) {
                inoutElements.splice(index, 1);
                inoutElementOptions.delete(element);
            }
        },

        // Remove fullinout element from tracking
        removeFullinoutElement: (element) => {
            const index = fullinoutElements.indexOf(element);
            if (index > -1) {
                fullinoutElements.splice(index, 1);
                fullinoutElementOptions.delete(element);
            }
        },

        // Clean up event listeners
        destroy: () => {
            if (scrollHandler) {
                window.removeEventListener('scroll', scrollHandler);
                scrollHandler = null;
            }
            elements = [];
            inoutElements = [];
            fullinoutElements = [];
            elementOptions.clear();
            inoutElementOptions.clear();
            fullinoutElementOptions.clear();
        }
    };

    // Initialize and return API
    init();
    return api;
};
